/**
 * CompanyContext — Memoria Global de Empresa Activa
 *
 * Almacena la empresa seleccionada por el usuario (o contador).
 * Persiste en localStorage (quipus_company) para sobrevivir refresh.
 * Fuente de verdad para Header, Setup, CierreDual, y cualquier módulo
 * que necesite saber "con qué empresa estoy trabajando ahora".
 */

import {
  createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, type ReactNode,
} from 'react';
import type { Company } from '../types';
import type { DBCompany } from '../repositories';
import { createRepositories } from '../repositories';
import { setActiveCompanyId } from '../services/AccountingEngine';
import { supabase } from '../services/supabaseClient';
import { auditAsCurrentUser } from '../utils/auditAsCurrentUser';
import { maybeFireTrialExpiringEmail } from '../services/TrialEmailService';
import { useUserRole } from './UserRoleContext';
import type { UserRole } from './UserRoleContext';
import { getEffectiveCompanyLimit, isOverrideExpired, LEGACY_PLAN_MAP } from '../utils/planLimits';

// ═══════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════

export interface Subscription {
  id: string;
  firm_id: string;
  plan: 'trial' | 'starter' | 'professional' | 'business' | 'emprendedor' | 'pro' | 'firma' | 'enterprise' | 'educativo';
  status: 'active' | 'expired' | 'cancelled';
  trial_ends_at: string | null;
  current_period_end: string | null;
  created_at: string;
  /** Límite base de empresas según plan (informativo — el límite efectivo se calcula vía planLimits.ts). */
  limite_empresas: number | null;
  /** Override manual de límite de empresas (e.g. Programa Fundador). null = sin override. */
  limite_empresas_override: number | null;
  /** Fecha de expiración del override. Pasada esta fecha, se ignora y se usa el límite del plan. */
  override_expires_at: string | null;
}

/**
 * Fix 2026-09-18: días restantes de trial, normalizado a medianoche UTC (no
 * a la hora exacta de registro) — así el contador decrementa en límites de
 * día calendario. Sin esta normalización, Math.ceil(6.3 días) = 7, y "día 2"
 * seguiría mostrando 7 hasta esa misma hora. Migrado desde TrialBanner.tsx
 * (que calculaba esto por su cuenta desde profiles.trial_ends_at) al migrar
 * el banner a leer de subscriptions — única fuente de verdad del trial.
 * Pura y exportada para poder testearla sin mocks — ver
 * CompanyContext.daysRemaining.test.ts.
 */
export function computeDaysRemaining(trialEndsAtIso: string | null): number {
  if (!trialEndsAtIso) return 0;
  const trialEndsAt = new Date(trialEndsAtIso);
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const endsUTC = Date.UTC(trialEndsAt.getUTCFullYear(), trialEndsAt.getUTCMonth(), trialEndsAt.getUTCDate());
  return Math.max(0, Math.round((endsUTC - todayUTC) / (1000 * 60 * 60 * 24)));
}

/**
 * Fix tq82 (2026-09-22): decide si corresponde disparar el email de "trial
 * por vencer" — y con qué datos — a partir de la MISMA fila `subscription`
 * que usan isTrialExpired/daysRemaining más abajo. Antes, el trigger vivía
 * en UserRoleContext.tsx y leía profiles.trial_ends_at/is_paid — un reloj
 * separado, desincronizado del que ya usan el banner y el gate desde tq81.
 * El parámetro es literalmente `Subscription | null` — no puede recibir
 * una fila de `profiles` por error de tipos. Pura y exportada para poder
 * testear la decisión sin renderizar el provider completo — ver
 * CompanyContext.trialEmailArgsFor.test.ts.
 */
export function trialEmailArgsFor(subscription: Subscription | null): { trialEndsAt: Date; isPaid: boolean } | null {
  if (!subscription?.trial_ends_at) return null;
  return {
    trialEndsAt: new Date(subscription.trial_ends_at),
    isPaid: subscription.status === 'active',
  };
}

interface CompanyContextType {
  /** Empresa actualmente seleccionada. null = sin empresa. */
  activeCompany: Company | null;
  /** Shortcut: activeCompany.companyId o null. */
  activeCompanyId: string | null;
  /** Shortcut: activeCompany.firmId o null. */
  activeFirmId: string | null;
  /** Reemplaza completamente la empresa activa y sincroniza localStorage. */
  setActiveCompany: (company: Company | null) => void;
  /** Actualiza campos parciales de la empresa activa. No-op si no hay empresa. */
  updateActiveCompany: (changes: Partial<Company>) => void;
  /**
   * Período activo de VISTA (YYYY-MM). Puede diferir del período base de la empresa.
   * Controla qué mes se muestra en todas las páginas (Compras, Dashboard, EEFF, etc.).
   * Actualizable desde el Header o desde la navegación de cualquier página.
   */
  filterPeriodo: string;
  setFilterPeriodo: (periodo: string) => void;
  /**
   * Vista de período: 'month' = YYYY-MM (default), 'year' = año completo.
   * Persiste en localStorage (quipus_period_mode).
   */
  periodMode: 'month' | 'year';
  setPeriodMode: (mode: 'month' | 'year') => void;
  /**
   * Año activo cuando periodMode === 'year' (e.g. '2026').
   * Persiste en localStorage (quipus_filter_year).
   */
  filterYear: string;
  setFilterYear: (year: string) => void;
  /** true when the currently-selected filterPeriodo is locked (closed) in periodBalances. */
  isFilterPeriodoClosed: boolean;
  /**
   * All companies visible to the current user.
   * For 'emprendedor' role: filtered to only their assigned company_id.
   * For 'contador' / 'admin': all companies for the firm (unfiltered).
   */
  companies: DBCompany[];
  /** true once the initial Supabase companies fetch has resolved. */
  companiesLoaded: boolean;
  /** Reload companies list from Supabase (call after insert/delete). */
  refreshCompanies: () => Promise<void>;
  /**
   * Permanently delete a company from Supabase and clear active state.
   * Does NOT cascade-delete related records (transactions, journal_entries, etc.)
   * — those must be cleaned up separately via Supabase Dashboard or a migration.
   */
  deleteCompany: (companyId: string) => Promise<void>;
  /**
   * Persist a brand-new company to Supabase (INSERT). Fix tq105 (2026-09-22):
   * setActiveCompany() only ever wrote to localStorage/React state —
   * repos.companies.create() existed but nothing called it, so a newly
   * created company never became a real `companies` row. It never showed up
   * in companies.getAll() after a refresh or from another session/device,
   * and any bank_accounts row referencing its company_id was silently
   * orphaned. Setup.tsx calls this when creating (not editing) a company,
   * before navigating away. Throws on failure (e.g. duplicate RUC within
   * the firm) — caller must surface the error, not swallow it.
   */
  createCompanySupabase: (company: Company) => Promise<void>;
  /**
   * Persist partial company fields to Supabase and sync local state.
   * Use this when Setup.tsx needs to save fields that setActiveCompany() skips.
   */
  updateCompanySupabase: (companyId: string, fields: {
    razonSocial?: string;
    regimen?: string;
    domicilioFiscal?: string;
    contadorWhatsapp?: string | null;
    contadorNombre?: string | null;
    contadorEmail?: string | null;
    tipoActividad?: 'comercio' | 'servicios' | 'manufactura' | 'mixto' | null;
    ingresosAnualesUIT?: number | null;
  }) => Promise<void>;
  /** Role of the authenticated user — sourced from profiles table. */
  userRole: UserRole;
  /** company_id from profiles (only set for emprendedor). */
  profileCompanyId: string | null;
  /** True when role is emprendedor (convenience flag for UI conditionals). */
  isEmprendedor: boolean;
  /** Current subscription row from Supabase, or null if none exists. */
  subscription: Subscription | null;
  /** True while the initial subscriptions fetch is in flight. */
  subscriptionLoading: boolean;
  /** True when the trial period has expired and no paid plan is active. */
  isTrialExpired: boolean;
  /** Days remaining in the trial (0 when expired or no trial_ends_at). */
  daysRemaining: number;
  /** Número de empresas activas del firm (companies.length). */
  companiesCount: number;
  /**
   * Límite efectivo de empresas para el plan actual (override vigente > plan).
   * null = sin límite (enterprise, o sin subscription/plan reconocido).
   */
  companyLimit: number | null;
  /** True cuando hubo un override de límite de empresas y ya venció. */
  companyLimitOverrideExpired: boolean;
  /**
   * Fila de plan_limits para el plan resuelto (solo los campos de gating de
   * features que necesita la UI hoy — no todo el schema de la tabla).
   * null mientras carga o si el plan no tiene fila en plan_limits.
   */
  planLimits: PlanLimitsRow | null;
  /** True mientras el fetch de plan_limits está en curso. */
  planLimitsLoading: boolean;
}

/** Subconjunto de columnas de `plan_limits` que gatean rutas/features en el cliente. */
export interface PlanLimitsRow {
  cierre_mensual_mes: number;
  asientos_automaticos_mes: number;
  audit_export_enabled: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// CONTEXTO
// ═══════════════════════════════════════════════════════════════════

const CompanyContext = createContext<CompanyContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════

/**
 * Empresa única a auto-seleccionar: solo cuando ya cargó la lista, no hay
 * empresa activa y el usuario tiene exactamente una. No depende de localStorage
 * (que se borra en cada SIGNED_OUT).
 */
export function pickSoleCompany(
  companies: DBCompany[],
  companiesLoaded: boolean,
  hasActiveCompany: boolean,
  alreadyDecided = false,
): DBCompany | null {
  if (alreadyDecided) return null;
  return companiesLoaded && !hasActiveCompany && companies.length === 1 ? companies[0] : null;
}

/** true si hay varias empresas y ninguna seleccionada — la UI debe pedir elegir una. */
export function needsCompanySelection(
  companiesLoaded: boolean,
  companiesCount: number,
  hasActiveCompany: boolean,
): boolean {
  return companiesLoaded && !hasActiveCompany && companiesCount > 1;
}

function currentMonthPeriodo(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function CompanyProvider({ children, userId }: { children: ReactNode; userId: string }) {
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(() => {
    try {
      const raw = localStorage.getItem('quipus_company');
      return raw ? (JSON.parse(raw) as Company) : null;
    } catch {
      return null;
    }
  });

  const [filterPeriodo, setFilterPeriodoState] = useState<string>(() => {
    const stored = localStorage.getItem('quipus_filter_periodo');
    if (stored) return stored;
    try {
      const raw = localStorage.getItem('quipus_company');
      const co = raw ? (JSON.parse(raw) as Company) : null;
      if (co?.periodo) return co.periodo;
    } catch {}
    return currentMonthPeriodo();
  });

  const [periodMode, setPeriodModeState] = useState<'month' | 'year'>(() => {
    const stored = localStorage.getItem('quipus_period_mode');
    return (stored === 'year' ? 'year' : 'month');
  });

  const [filterYear, setFilterYearState] = useState<string>(() => {
    return localStorage.getItem('quipus_filter_year') ?? String(new Date().getFullYear());
  });

  // Role from profiles table (for company filtering)
  const { role: userRole, profileCompanyId } = useUserRole();
  const isEmprendedor = userRole === 'emprendedor';

  // Companies list loaded from Supabase
  const [allCompanies, setAllCompanies] = useState<DBCompany[]>([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const repos = useMemo(() => createRepositories(), []);
  // true tras la primera evaluación del auto-select de empresa única (ver efecto más abajo).
  const soleDecidedRef = useRef(false);

  // Subscription state
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const subscriptionFetchedRef = useRef(false);

  useEffect(() => {
    if (!userId || subscriptionFetchedRef.current) return;
    subscriptionFetchedRef.current = true;
    void (async () => {
      try {
        const { data } = await supabase
          .from('subscriptions')
          .select('id,plan,status,trial_ends_at,current_period_end,created_at,firm_id,limite_empresas,limite_empresas_override,override_expires_at')
          .eq('firm_id', userId)
          .maybeSingle();
        const sub = data as Subscription | null;
        setSubscription(sub);
        // Fix tq82 (2026-09-22): antes este trigger vivía en
        // UserRoleContext.tsx y leía profiles.trial_ends_at/is_paid — un
        // reloj separado y desincronizado del que usan isTrialExpired/
        // daysRemaining acá abajo. trialEmailArgsFor() decide con la MISMA
        // fila `subscription` que se acaba de resolver — una sola fuente
        // de verdad, nunca profiles.
        const emailArgs = trialEmailArgsFor(sub);
        if (emailArgs) void maybeFireTrialExpiringEmail(emailArgs.trialEndsAt, emailArgs.isPaid);
      } catch {
        setSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    })();
  }, [userId]);

  // plan_limits — solo se resuelve una vez que sabemos el plan real
  // (subscription.plan). RLS es SELECT-only para authenticated (ver
  // migración 2026-08-16) — lectura de metadata de plan, no de datos del
  // usuario, por eso no hace falta filtrar por firm_id acá.
  const [planLimits, setPlanLimits] = useState<PlanLimitsRow | null>(null);
  const [planLimitsLoading, setPlanLimitsLoading] = useState(true);

  useEffect(() => {
    if (!subscription?.plan) {
      setPlanLimitsLoading(false);
      return;
    }
    let mounted = true;
    setPlanLimitsLoading(true);
    const resolvedPlan = LEGACY_PLAN_MAP[subscription.plan] ?? subscription.plan;
    supabase
      .from('plan_limits')
      .select('cierre_mensual_mes, asientos_automaticos_mes, audit_export_enabled')
      .eq('plan', resolvedPlan)
      .maybeSingle()
      .then(({ data }) => {
        if (mounted) {
          setPlanLimits(data as PlanLimitsRow | null);
          setPlanLimitsLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [subscription?.plan]);

  // ── Trial gate — ÚNICA fuente de verdad: la fila `subscriptions` ──
  // Fail-closed (2026-08-14): sin fila confirmada en subscriptions ya NO es
  // acceso permitido — antes dejaba pasar "admin/owner/cuenta legacy" por
  // default, que era el mismo patrón fail-open de los bugs de handle_new_user()
  // y profiles.plan DEFAULT cerrados hoy. hola@tryquipus.com nunca llega a
  // este gate — App.tsx lo bypasea antes de montar WaitlistGate/TrialGate.
  // Cuentas legacy legítimas sin fila (ej. deliapebe18@hotmail.com) requieren
  // backfill explícito en subscriptions — ver
  // supabase/migrations/20260814000002_backfill_delia_subscription.sql.
  // Reglas de acceso (OR):
  //   1. status === 'active'             → acceso permitido
  //   2. trial_ends_at > NOW()           → acceso permitido (trial vigente)
  //   3. sin fila, o cualquier otro caso → bloqueado
  const isTrialExpired = useMemo<boolean>(() => {
    // Sin fila en subscriptions → bloqueado.
    if (!subscription) return true;
    // 1. Suscripción activa (pagada o en trial) → acceso.
    if (subscription.status === 'active') return false;
    // 2. Ventana de trial aún vigente por fecha → acceso.
    if (subscription.trial_ends_at &&
        new Date(subscription.trial_ends_at).getTime() > Date.now()) {
      return false;
    }
    // 3. Caso contrario → trial expirado.
    return true;
  }, [subscription]);

  const daysRemaining = useMemo<number>(
    () => computeDaysRemaining(subscription?.trial_ends_at ?? null),
    [subscription],
  );

  // ── Security guard: clear stale localStorage data from a different firm ──
  // When a new user logs in, localStorage may contain another user's company.
  // activeCompany.firmId !== userId means the cached data belongs to someone else.
  // Clear it immediately — firm_id MUST always equal auth.uid().
  useEffect(() => {
    if (!activeCompany) return;
    const cachedFirmId = activeCompany.firmId;
    if (cachedFirmId && cachedFirmId !== userId) {
      console.warn(
        '[Security] Clearing stale company from different firm.',
        'cached:', cachedFirmId, '≠ current user:', userId,
      );
      setActiveCompany(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only re-run when userId changes (login/logout)

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    repos.companies.getAll(userId)
      .then(list => {
        if (!cancelled) { setAllCompanies(list); setCompaniesLoaded(true); }
      })
      .catch(err => {
        if (!cancelled) { console.error('[CompanyContext] getAll failed:', err); setCompaniesLoaded(true); }
      });
    return () => { cancelled = true; };
  }, [userId, repos]);

  const refreshCompanies = useCallback(async () => {
    if (!userId) return;
    const list = await repos.companies.getAll(userId);
    setAllCompanies(list);
  }, [userId, repos]);

  // Emprendedor only sees their assigned company; contador/admin see all.
  const companies = useMemo<DBCompany[]>(() => {
    if (isEmprendedor && profileCompanyId) {
      return allCompanies.filter(c => c.companyId === profileCompanyId);
    }
    return allCompanies;
  }, [allCompanies, isEmprendedor, profileCompanyId]);

  // Auto-select the emprendedor's company once the list is loaded.
  useEffect(() => {
    if (!isEmprendedor || !companiesLoaded || companies.length === 0) return;
    if (!activeCompany || (profileCompanyId && activeCompany.companyId !== profileCompanyId)) {
      const theirs = companies[0];
      setActiveCompany({
        companyId: theirs.companyId,
        firmId:    theirs.firmId ?? userId,
        name:      theirs.name,
        ruc:       theirs.ruc,
        periodo:   theirs.periodo ?? currentMonthPeriodo(),
        regimen:   (theirs.regimen as Company['regimen']) ?? 'general',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmprendedor, companiesLoaded, companies.length]);

  // Contador/admin con una sola empresa y sin selección (p. ej. localStorage
  // borrado tras un SIGNED_OUT): seleccionarla automáticamente.
  // Una sola vez por carga de lista: si un `setActiveCompany(null)` posterior
  // (p. ej. deleteCompany) re-disparara este efecto, no debe pisar la decisión.
  // deleteCompany reinicia la bandera para que, al quedar 1 empresa, se auto-seleccione.
  useEffect(() => {
    if (isEmprendedor) return; // el emprendedor tiene su propio auto-select arriba
    if (!companiesLoaded) return;
    const sole = pickSoleCompany(companies, companiesLoaded, !!activeCompany, soleDecidedRef.current);
    soleDecidedRef.current = true;
    if (!sole) return;
    setActiveCompany({
      companyId: sole.companyId,
      firmId:    sole.firmId ?? userId,
      name:      sole.name,
      ruc:       sole.ruc,
      periodo:   sole.periodo ?? currentMonthPeriodo(),
      regimen:   (sole.regimen as Company['regimen']) ?? 'general',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmprendedor, companiesLoaded, companies]);

  function setFilterPeriodo(periodo: string) {
    setFilterPeriodoState(periodo);
    localStorage.setItem('quipus_filter_periodo', periodo);
  }

  function setPeriodMode(mode: 'month' | 'year') {
    setPeriodModeState(mode);
    localStorage.setItem('quipus_period_mode', mode);
    // When switching to year, seed filterYear from the current filterPeriodo
    if (mode === 'year') {
      const yr = filterPeriodo.split('-')[0];
      setFilterYearState(yr);
      localStorage.setItem('quipus_filter_year', yr);
    }
  }

  function setFilterYear(year: string) {
    setFilterYearState(year);
    localStorage.setItem('quipus_filter_year', year);
  }

  function setActiveCompany(company: Company | null) {
    setActiveCompanyState(company);
    if (company) {
      localStorage.setItem('quipus_company', JSON.stringify(company));
      localStorage.setItem('quipus_active_company_id', company.companyId);
    } else {
      localStorage.removeItem('quipus_company');
      localStorage.removeItem('quipus_active_company_id');
    }
  }

  function updateActiveCompany(changes: Partial<Company>) {
    if (!activeCompany) return;
    setActiveCompany({ ...activeCompany, ...changes });
  }

  async function deleteCompany(companyId: string) {
    await repos.companies.delete(userId, companyId);
    // Trazabilidad: borrado (soft) de una empresa — repos.companies.delete solo marca is_deleted.
    void auditAsCurrentUser(userId, {
      action:      'deleted_soft',
      entityType:  'company',
      entityId:    companyId,
      changesJson: { companyId },
    });
    setActiveCompany(null);
    // Si tras borrar queda una sola empresa, el efecto de auto-select debe volver a evaluarse.
    soleDecidedRef.current = false;
    await refreshCompanies();
  }

  async function createCompanySupabase(company: Company) {
    await repos.companies.create(company);
    await refreshCompanies();
  }

  async function updateCompanySupabase(companyId: string, fields: {
    razonSocial?: string;
    regimen?: string;
    domicilioFiscal?: string;
    contadorWhatsapp?: string | null;
    contadorNombre?: string | null;
    contadorEmail?: string | null;
    tipoActividad?: 'comercio' | 'servicios' | 'manufactura' | 'mixto' | null;
    ingresosAnualesUIT?: number | null;
  }) {
    await repos.companies.update(userId, companyId, fields);
    // Sync changes into local state so UI reflects them immediately
    if (activeCompany?.companyId === companyId) {
      const patch: Partial<Company> = {};
      if (fields.razonSocial       !== undefined) patch.name             = fields.razonSocial;
      if (fields.regimen            !== undefined) patch.regimen          = fields.regimen as Company['regimen'];
      if (fields.contadorWhatsapp  !== undefined) patch.contadorWhatsapp = fields.contadorWhatsapp ?? undefined;
      if (fields.contadorNombre    !== undefined) patch.contadorNombre   = fields.contadorNombre   ?? undefined;
      if (fields.contadorEmail     !== undefined) patch.contadorEmail    = fields.contadorEmail    ?? undefined;
      if (fields.tipoActividad     !== undefined) patch.tipoActividad    = fields.tipoActividad    ?? undefined;
      if (fields.ingresosAnualesUIT !== undefined) patch.ingresosAnualesUIT = fields.ingresosAnualesUIT ?? undefined;
      updateActiveCompany(patch);
    }
  }

  // Sincroniza el Identity Guard del AccountingEngine cada vez que
  // cambia la empresa activa (incluyendo la carga inicial desde localStorage).
  useEffect(() => {
    setActiveCompanyId(activeCompany?.companyId ?? null);
  }, [activeCompany]);

  // Sincroniza filterPeriodo al período base cuando el usuario cambia de empresa.
  useEffect(() => {
    if (activeCompany?.periodo) {
      setFilterPeriodo(activeCompany.periodo);
    }
  }, [activeCompany?.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reactive check: is the currently-selected period closed?
  const _firmId    = userId;
  const _companyId = activeCompany?.companyId ?? null;
  const periodNorm = filterPeriodo ? filterPeriodo.replace(/-/g, '').slice(0, 6) : '';

  const [isFilterPeriodoClosed, setIsFilterPeriodoClosed] = useState(false);

  useEffect(() => {
    if (!_firmId || !_companyId || !periodNorm) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      supabase
        .from('period_balances')
        .select('is_closed')
        .eq('firm_id', _firmId)
        .eq('company_id', _companyId)
        .eq('period', periodNorm)
        .eq('is_closed', true)
        .limit(1)
        .then(({ data }) => {
          if (!cancelled) setIsFilterPeriodoClosed((data?.length ?? 0) > 0);
        });
    }, 300);
    return () => { clearTimeout(timer); cancelled = true; };
  }, [_firmId, _companyId, periodNorm]);

  const companyLimit = useMemo(() => getEffectiveCompanyLimit(subscription), [subscription]);
  const companyLimitOverrideExpired = useMemo(() => isOverrideExpired(subscription), [subscription]);

  return (
    <CompanyContext.Provider value={{
      activeCompany,
      activeCompanyId: activeCompany?.companyId ?? null,
      activeFirmId: userId,
      setActiveCompany,
      updateActiveCompany,
      filterPeriodo,
      setFilterPeriodo,
      periodMode,
      setPeriodMode,
      filterYear,
      setFilterYear,
      isFilterPeriodoClosed,
      companies,
      companiesLoaded,
      refreshCompanies,
      deleteCompany,
      createCompanySupabase,
      updateCompanySupabase,
      userRole,
      profileCompanyId,
      isEmprendedor,
      subscription,
      subscriptionLoading,
      isTrialExpired,
      daysRemaining,
      companiesCount: companies.length,
      companyLimit,
      companyLimitOverrideExpired,
      planLimits,
      planLimitsLoading,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════

export function useCompanyContext(): CompanyContextType {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompanyContext must be used within <CompanyProvider>');
  return ctx;
}
